import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import Card from '../Card/Card';
import CardOverlay from '../CardOverlay/CardOverlay';
import { getConfig } from './configs';
import styles from './CardGroup.module.css';

const CardGroup = ({
    size = "Desktop 1440",
    cardCount = 5,
    groupPick12 = 1,
    groupPick7 = 1,
    cardId1,
    cardId2,
    cardId3,
    cardId4,
    cardId5,
    onCardSelect,
    isVisible = true
}) => {
    const location = useLocation();
    const initialCardIds = [cardId1, cardId2, cardId3, cardId4, cardId5].slice(0, cardCount);
    const [defaultOpenIndex, setDefaultOpenIndex] = useState(null);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [selectedCard, setSelectedCard] = useState(null);
    const groupRef = useRef(null);
    const videoRefs = useRef({});

    const getCardConfig = useCallback(() => {
        return getConfig(size, cardCount, groupPick12, groupPick7);
    }, [size, cardCount, groupPick12, groupPick7]);

    const config = getCardConfig();

    useEffect(() => {
        const defaultIndex = config.findIndex(item => item.io);
        setDefaultOpenIndex(defaultIndex);
    }, [config]);

    // Manejo optimizado de videos
    useEffect(() => {
        const videos = Object.values(videoRefs.current);
        
        if (isVisible) {
            videos.forEach(video => {
                if (video) {
                    video.load(); // Asegura que el video se cargue
                    if (video.paused) {
                        video.play().catch(() => {}); // Manejo silencioso de errores de autoplay
                    }
                }
            });
        } else {
            videos.forEach(video => {
                if (video) {
                    video.pause();
                    video.currentTime = 0;
                }
            });
        }

        return () => {
            videos.forEach(video => {
                if (video) {
                    video.pause();
                    video.currentTime = 0;
                }
            });
        };
    }, [isVisible]);

    const handleMouseEnter = useCallback((index) => {
        if ((size === "Desktop 1440" || size === "Desktop 1200") && isVisible) {
            setHoveredIndex(index);
        }
    }, [size, isVisible]);

    const handleMouseLeave = useCallback(() => {
        if (size === "Desktop 1440" || size === "Desktop 1200") {
            setHoveredIndex(null);
        }
    }, [size]);

    const handleCardSelect = useCallback((cardId) => {
        setSelectedCard(cardId);
        if (onCardSelect) {
            onCardSelect(cardId);
        }
    }, [onCardSelect]);

    const handleOverlayClose = useCallback(() => {
        if (location.pathname === '/') {
            window.history.replaceState(null, '', '/');
        }
        setSelectedCard(null);
    }, [location.pathname]);

    const isCardOpen = useCallback((index) => {
        if (size === "Phone") return true;
        if (hoveredIndex !== null) return index === hoveredIndex;
        return index === defaultOpenIndex;
    }, [hoveredIndex, defaultOpenIndex, size]);

    const setVideoRef = useCallback((element, index) => {
        if (element) {
            videoRefs.current[index] = element;
        }
    }, []);

    return (
        <motion.div 
            ref={groupRef}
            className={`${styles.cardGroup} ${styles[size.toLowerCase().replace(' ', '')]} ${styles[`cards${cardCount}`]}`}
            onMouseLeave={handleMouseLeave}
            initial={false}
            animate={{
                opacity: isVisible ? 1 : 0.3,
                scale: isVisible ? 1 : 0.98
            }}
            transition={{
                duration: 0.3,
                ease: "easeInOut"
            }}
        >
            {initialCardIds.map((cardId, index) => (
                cardId ? (
                    <div
                        key={cardId}
                        className={styles.cardWrapper}
                        onMouseEnter={() => handleMouseEnter(index)}
                    >
                        <Card
                            cardId={cardId}
                            size={size === "Desktop 1200" ? "Small" : "Grande"}
                            initialOpen={isCardOpen(index)}
                            disableHover={size === "Phone" || size === "Tablet"}
                            autoPlay={isVisible}
                            hoverPlay={size === "Desktop 1440" || size === "Desktop 1200"}
                            controls={false}
                            loop={true}
                            rotation={config[index]?.r || 0}
                            onCardSelect={handleCardSelect}
                            isControlledByGroup={true}
                            setVideoRef={(el) => setVideoRef(el, index)}
                        />
                    </div>
                ) : null
            ))}
            {selectedCard && (
                <CardOverlay
                    cardId={selectedCard}
                    onClose={handleOverlayClose}
                />
            )}
        </motion.div>
    );
};

export default React.memo(CardGroup);